// MetaAdvertisingPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from './paidxs.png'

const MetaAdvertisingPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const metaStats = [
        { number: '3.07B', label: 'monthly active users across Meta platforms' },
        { number: '2.35B', label: 'daily active Facebook users globally' },
        { number: '2.3x', label: 'higher ROI from optimized Meta campaigns' },
        { number: '71%', label: 'of marketers consider Meta ads essential for business' }
    ];

    const metaAdvertisingApproaches = [
        {
            icon: 'fas fa-chess',
            title: 'Strategy & Media Planning',
            desc: 'Comprehensive audience research and targeting strategy, goal-aligned media planning, and cross-channel allocation across Facebook, Instagram, and Messenger for optimal reach.'
        },
        {
            icon: 'fas fa-paint-brush',
            title: 'Creative Development & Testing',
            desc: 'Tailored ad asset creation including static images, carousels, video content, Story & Reels templates, and copy variations designed to maximize engagement and conversions.'
        },
        {
            icon: 'fas fa-bullseye',
            title: 'Precision Targeting & Segmentation',
            desc: 'Advanced audience segmentation using first-party data, custom audiences, lookalike modeling, and behavioral signals to deliver ads to the right people at optimal times.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Performance Optimization & Bid Management',
            desc: 'Real-time campaign monitoring with strategic bid adjustments, creative rotation optimization, audience performance tuning, and ROAS maximization techniques.'
        },
        {
            icon: 'fas fa-chart-bar',
            title: 'Reporting & Insights Analytics',
            desc: 'Comprehensive performance reporting with actionable insights across key metrics including CTR, CPM, conversions, and ROI for data-driven decision making.'
        },
        {
            icon: 'fas fa-graduation-cap',
            title: 'Consultancy & Team Support',
            desc: 'Expert guidance, performance audits, and in-house team training services to complement internal capabilities and build sustainable Meta advertising expertise.'
        }
    ];

    const benefits = [
        ['Precision Audience Targeting', 'Ads delivered to highly relevant audiences based on interests, behavior, and intent signals'],
        ['Scalable Multi-Platform Reach', 'Integrated campaigns across Facebook, Instagram, Messenger, and Meta\'s full ecosystem'],
        ['Conversion-Focused Campaign Delivery', 'Optimized traffic flows that drive meaningful actions and measurable business outcomes'],
        ['Creative & Testing Optimization', 'Continuous creative refinement based on real-time performance data and audience response'],
        ['Transparent Performance Insights', 'Clear, actionable data and reporting tied directly to business objectives and KPIs']
    ];

    const targetBusinesses = [
        'E-commerce brands seeking to drive online sales and customer acquisition',
        'Service-based businesses building brand awareness and lead generation pipelines',
        'B2B & B2C companies expanding customer reach and market penetration',
        'Local businesses attracting foot traffic, calls, and local engagement',
        'Product-focused companies benefiting from visual engagement and storytelling',
        'Growth-focused organizations scaling conversions and nurturing customer loyalty'
    ];

    const metaAdvertisingQuestions = [
        'How can you reach your ideal customers across Meta\'s vast ecosystem?',
        'What creative approaches drive the highest engagement and conversions?',
        'How do you optimize ad spend for maximum return on investment?',
        'What audience segmentation strategies deliver the best targeting precision?',
        'How can you leverage Meta\'s AI-powered optimization tools effectively?'
    ];

    return (
        <div className="paidmedia sv-page">
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Meta Advertising</span> Services
                    </h1>
                    <p>Strategic campaigns across Facebook, Instagram & Meta's full ecosystem for measurable growth</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Meta Advertising gives you direct access to some of the world's most powerful social platforms — including Facebook, Instagram, Messenger, and WhatsApp — helping your brand connect with targeted audiences at every stage of the customer journey.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>Meta Advertising services</strong> combine strategic insight, audience precision, and performance optimisation to deliver campaigns that scale visibility, engagement, and conversions across Meta's interconnected ecosystem.
                                </p>
                                <p className="sv-description">
                                    Meta's advertising network powers ads across multiple social touchpoints used daily by billions of people. This creates a unique opportunity to reach highly specific audiences based on interests, behavior, and intent — helping you increase brand awareness, drive meaningful traffic, and measure performance with transparent data insights.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Launch Meta Campaign
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-chart-line mr-2"></i>
                                        Get Meta Advertising Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Meta Advertising Advantage</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {metaStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Meta Advertising Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Meta Advertising?</h2>
                                <p className="sv-section-description">
                                    Meta Advertising encompasses strategic campaign management across Meta's full ecosystem — Facebook, Instagram, Messenger, and WhatsApp — leveraging advanced targeting, creative optimization, and AI-powered delivery to reach specific audiences with precision and impact.
                                </p>
                                <p className="sv-section-description">
                                    In today's connected social landscape, Meta's robust advertising infrastructure lets you tailor campaigns for brand awareness, lead generation, traffic growth, or direct sales — all with measurable performance outcomes and transparent insights.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Meta Advertising Questions We Address:</h4>
                                    {metaAdvertisingQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Meta Advertising Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Meta Advertising Drives Social Success</h2>
                                <p className="sv-section-description">
                                    Meta Advertising transforms social presence into measurable business outcomes, enabling brands to move from random posting to strategic, performance-driven social marketing.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Precision Audience Targeting</h5>
                                        <p>Reach highly specific demographics, interests, and behavioral segments</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-layer-group"></i>
                                        <h5>Multi-Platform Integration</h5>
                                        <p>Seamless campaigns across Facebook, Instagram, and Messenger ecosystems</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Performance Optimization</h5>
                                        <p>AI-driven bid strategies and real-time campaign adjustments</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-eye"></i>
                                        <h5>Visual Engagement Focus</h5>
                                        <p>Leverage Stories, Reels, and immersive formats for higher engagement</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Meta Advertising Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Meta Advertising Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We offer a full suite of Meta Advertising services designed to support brands at every step of the advertising lifecycle — from strategic planning and creative development to optimization and performance reporting.
                                </p>

                                <div className="sv-approaches-grid">
                                    {metaAdvertisingApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Meta Advertising with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Meta Advertising?</h2>
                                <p className="sv-section-description">
                                    Meta Advertising delivers maximum value for organizations seeking to leverage social platforms for measurable business growth and customer engagement:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're launching a new product, scaling conversions, or nurturing customer loyalty, Meta Advertising provides the strategic tools and audience reach to grow with measurable impact.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Supercharge Your Meta Advertising?</h2>
                                <p className="sv-cta-description">
                                    Meta Advertising has become one of the most effective ways to grow reach, engagement, and conversions in the modern digital landscape. If you want campaigns that perform, evolve, and scale with your business, Reachtix's <strong>Meta Advertising services</strong> are your strategic advantage.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat Meta Advertising as a growth engine — not just an advertising channel. Our specialists blend platform expertise with business insight to make your social advertising a powerful driver of measurable growth.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-facebook mr-2"></i>
                                        Launch Meta Campaign
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Meta Advertising Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default MetaAdvertisingPage;